import { DeltaServerWebSocketClient } from '@hds-products/delta-server-client';
import { socketCredentials } from '@hds/identity-client-sdk';
import { useLogger } from '../adapters/ports.js';

// The second of the app's two sockets — the first is the host's own chat socket,
// supplied as the realtime port, whose event vocabulary this deliberately shares
// so socket health reads as one dashboard filtered by `socket`, not as two
// unrelated sets of names.
const SOCKET = 'delta-server';

// Reconnecting wrapper over the event-runtime delta-server websocket.
//
// The reused DeltaServerWebSocketClient speaks the `frank_delta.v1` protocol
// (sends `subscribe-to-topic` frames and auto-ACKs inbound `delta` frames).
// Because the delta-server opens its Mongo change stream with
// `fullDocument: "updateLookup"`, every `delta` frame carries the changed
// document in `changeEvent.fullDocument` — so consumers get a whole entity per
// change, no client-side patching required. A subscriber that only reads a few
// fields off a heavy document can pass a projection to have the server trim the
// frame first; what arrives is then the entity minus the excluded fields.
//
// The base client has no reconnect, so we add it here (mirroring the cadence in
// services/realtime.js) plus automatic re-subscription of every topic onto each
// fresh socket. One socket-level handler per subscription fans out to N
// subscribers.
const RECONNECT_INITIAL_DELAY_MS = 1_000;
const RECONNECT_MAX_DELAY_MS = 30_000;
const RECONNECT_BACKOFF_FACTOR = 2;
// The server's word for "this principal was revoked" — a sign-out, or a revoked
// key. Reconnecting with the same session would only be refused again.
const CLOSE_CODE_REFUSED = 4403;
// The server's word for "I could not accept your credentials" — a token or proof it
// would not take, or a proof made for an address it does not answer on.
const CLOSE_CODE_UNAUTHENTICATED = 4401;
// A dropped socket is routine; a feed that has failed this many times in a row is not,
// and the person is told so.
const UNAVAILABLE_AFTER_ATTEMPTS = 3;

class DeltaRealtimeClient {
  #wsUrl = null;
  #client = null;
  #subscriptions = new Map();   // subscription key -> record
  #socketSubscriptionKeys = new Map();   // keys already subscribed on the live socket -> their socket handler
  #reconnectTimer = null;
  #reconnectDelay = RECONNECT_INITIAL_DELAY_MS;
  #reconnectAttempt = 0;
  #userDisconnected = false;
  #suspended = false;
  #connecting = false;
  #refused = false;
  #unavailable = false;
  #lastCloseCode = null;
  #statusHandlers = new Set();
  #reconnectHandlers = new Set();
  // A socket that opened and then dropped; the next open must tell the reconnect handlers.
  #socketOpened = false;
  #droppedSinceOpen = false;

  // Whether the feed has failed enough times in a row that the person should be told. Handlers
  // are called with `{ available }` when that changes, and once at once with the current state.
  onStatus(handler) {
    this.#statusHandlers.add(handler);
    try { handler({ available: !this.#unavailable }); } catch { /* noop */ }
    return () => this.#statusHandlers.delete(handler);
  }

  // Changes made while the socket was down are never sent, so a screen must load again. Handlers
  // are called each time a socket opens after an open one dropped: not on the first open, and not
  // on a resume, which the app answers with its own load.
  onReconnect(handler) {
    this.#reconnectHandlers.add(handler);
    return () => this.#reconnectHandlers.delete(handler);
  }

  #notifyReconnect() {
    for (const handler of this.#reconnectHandlers) {
      Promise.resolve()
        .then(() => handler())
        .catch((err) => useLogger().warn(
          { event: 'ws.reconnect_handler_failed', socket: SOCKET, err },
          'delta reconnect handler failed'
        ));
    }
  }

  #setUnavailable(unavailable) {
    if (this.#unavailable === unavailable) return;
    this.#unavailable = unavailable;
    for (const handler of this.#statusHandlers) {
      try { handler({ available: !unavailable }); } catch { /* noop */ }
    }
  }

  // Say what went wrong in words that point at the cause, not just at the symptom.
  #reportUnavailable() {
    const code = this.#lastCloseCode;
    let cause = 'the server could not be reached';
    if (code === CLOSE_CODE_UNAUTHENTICATED) {
      cause = 'the server refused this device\'s sign-in proof (close code 4401). '
        + `If the address is not the one the server was told to answer on, add it to the delta server's DELTA_SERVER_PUBLIC_URL`;
    } else if (code === CLOSE_CODE_REFUSED) {
      cause = 'the server revoked this session (close code 4403), so sign in again';
    } else if (code) {
      cause = `the server closed the socket with code ${code}`;
    }
    useLogger().error(
      { event: 'ws.unavailable', socket: SOCKET, url: this.#wsUrl, attempts: this.#reconnectAttempt, code },
      `live feed cannot be established at ${this.#wsUrl}: ${cause}`
    );
  }

  connect(wsUrl) {
    if (!wsUrl) return;
    // A no-op when we're already wired to this endpoint and live.
    if (this.#client && this.#wsUrl === wsUrl && this.#isConnected()) return;
    this.#wsUrl = wsUrl;
    this.#userDisconnected = false;
    this.#refused = false;
    this.#open();
  }

  disconnect() {
    this.#userDisconnected = true;
    this.#clearReconnect();
    this.#teardownSocket();
  }

  // Close the socket for an app backgrounding — keeps topics/subscribers and the
  // ws url so resume() can reopen and re-subscribe. Distinct from disconnect(),
  // which is logout semantics. While suspended, drops don't schedule reconnects.
  suspend() {
    if (this.#suspended) return;
    this.#suspended = true;
    this.#socketOpened = false;
    this.#clearReconnect();
    this.#teardownSocket();
  }

  // Reopen after a foreground resume. No-op if never connected (no ws url) or if
  // the user explicitly disconnected. #open() re-subscribes every tracked topic.
  resume() {
    if (!this.#suspended) return;
    this.#suspended = false;
    this.#open();
  }

  // Register a handler for a delta topic (e.g. 'item-bidding'), optionally with
  // a projection trimming each frame server-side. Returns an unsubscribe fn.
  // Handlers receive the raw `delta` frame; read the changed document off
  // `frame.changeEvent.fullDocument` (minus anything the projection excluded).
  subscribe(topic, handler, projection) {
    if (!topic || typeof handler !== 'function') return () => {};
    const subscriptionKey = this.#subscriptionKeyFor(topic, projection);
    let subscription = this.#subscriptions.get(subscriptionKey);
    if (!subscription) {
      subscription = { topic, projection, handlers: new Set() };
      this.#subscriptions.set(subscriptionKey, subscription);
    }
    subscription.handlers.add(handler);
    // If the socket is already live, subscribe this topic now; otherwise it gets
    // picked up when the socket opens.
    if (this.#isConnected()) this.#subscribeTopicOnSocket(subscriptionKey, subscription);
    return () => {
      const existing = this.#subscriptions.get(subscriptionKey);
      if (!existing) return;
      existing.handlers.delete(handler);
      if (existing.handlers.size === 0) {
        this.#subscriptions.delete(subscriptionKey);
        // Tell the server too, or it keeps streaming frames nobody reads
        this.#unsubscribeTopicOnSocket(subscriptionKey, existing);
      }
    };
  }

  // A subscription is identified by topic AND projection, never topic alone: the
  // delta-server opens a distinct change stream per (topic, projection) pair and
  // stamps a derived name — `${topic}-${base64(projection)}` — on every frame it
  // publishes, which is the name the client dispatches on. Two projections of one
  // collection are two independent topics on the wire, so bucketing them
  // separately here mirrors the server and keeps a projected subscriber from
  // colliding with an unprojected one. This key is internal — it only has to be
  // stable and unique, so it skips the base64 and stays legible in a debugger.
  #subscriptionKeyFor(topic, projection) {
    if (!projection) return topic;
    return `${topic}-${JSON.stringify(projection)}`;
  }

  #isConnected() {
    try { return !!this.#client && this.#client.isConnected(); } catch { return false; }
  }

  async #open() {
    if (this.#userDisconnected || this.#suspended || !this.#wsUrl || this.#connecting) return;
    if (this.#isConnected()) return;
    this.#connecting = true;
    let client;
    try {
      // The socket authenticates with its first frame: the session's token and a
      // proof for this handshake, read fresh on every connection.
      client = new DeltaServerWebSocketClient(this.#wsUrl, undefined, (url) => socketCredentials(url));
    } catch (err) {
      this.#connecting = false;
      useLogger().error(
        { event: 'ws.construct_failed', socket: SOCKET, err },
        'delta client could not be constructed'
      );
      this.#scheduleReconnect();
      return;
    }
    this.#client = client;
    this.#socketSubscriptionKeys.clear();
    // The base client tears down its own socket on close/error; we just need to
    // reconnect (unless the user asked us to stop).
    client.on('close', (event) => this.#onDrop('close', event));
    client.on('error', () => this.#onDrop('error'));
    try {
      await client.whenConnected();
      this.#connecting = false;
      useLogger().info(
        {
          event: 'ws.open',
          socket: SOCKET,
          attempt: this.#reconnectAttempt,
          topics: this.#subscriptions.size
        },
        'delta socket open'
      );
      this.#reconnectDelay = RECONNECT_INITIAL_DELAY_MS;
      this.#reconnectAttempt = 0;
      this.#lastCloseCode = null;
      this.#setUnavailable(false);
      for (const [subscriptionKey, subscription] of this.#subscriptions) {
        this.#subscribeTopicOnSocket(subscriptionKey, subscription);
      }
      this.#socketOpened = true;
      if (this.#droppedSinceOpen) {
        this.#droppedSinceOpen = false;
        this.#notifyReconnect();
      }
    } catch (err) {
      this.#connecting = false;
      // Was swallowed entirely. A handshake that never completes and a socket
      // that drops after opening are different failures with the same symptom —
      // a board that stops updating — and only this one names which.
      useLogger().error(
        { event: 'ws.open_failed', socket: SOCKET, err },
        'delta socket never completed its handshake'
      );
      this.#scheduleReconnect();
    }
  }

  // Re-subscribing after a reconnect MUST replay the original projection, not
  // just the topic: the base client registers its handler under the projection-
  // derived name the server stamps on outbound frames. Re-subscribing bare would
  // listen on `item-bidding` while the server keeps publishing to
  // `item-bidding-<base64>`, so frames would silently stop dispatching — a live
  // board that quietly freezes after one dropped socket. Reading the projection
  // back off the retained subscription record (rather than a closure captured at
  // subscribe() time) is what makes the reconnect path replay it verbatim.
  #subscribeTopicOnSocket(subscriptionKey, subscription) {
    if (!this.#client || this.#socketSubscriptionKeys.has(subscriptionKey)) return;
    const onFrame = (frame) => this.#dispatch(subscriptionKey, frame);
    this.#socketSubscriptionKeys.set(subscriptionKey, onFrame);
    if (subscription.projection) {
      this.#client.subscribeToTopicWithProjection(subscription.topic, subscription.projection, onFrame);
    } else {
      this.#client.subscribeToTopic(subscription.topic, onFrame);
    }
  }

  #unsubscribeTopicOnSocket(subscriptionKey, subscription) {
    const onFrame = this.#socketSubscriptionKeys.get(subscriptionKey);
    if (!onFrame) return;
    this.#socketSubscriptionKeys.delete(subscriptionKey);
    if (!this.#isConnected()) return;
    try {
      if (subscription.projection) {
        this.#client.unsubscribeFromTopicWithProjection(subscription.topic, subscription.projection, onFrame);
      } else {
        this.#client.unsubscribeFromTopic(subscription.topic, onFrame);
      }
    } catch (err) {
      useLogger().warn(
        { event: 'ws.unsubscribe_failed', socket: SOCKET, topic: subscription.topic, err },
        'delta unsubscribe failed'
      );
    }
  }

  #dispatch(subscriptionKey, frame) {
    const subscription = this.#subscriptions.get(subscriptionKey);
    // The socket-level handler outlives the last unsubscribe, so a frame can
    // still land after the bucket is gone.
    if (!subscription) return;
    for (const handler of subscription.handlers) {
      try { handler(frame); } catch (err) {
        useLogger().warn(
          { event: 'ws.subscriber_failed', socket: SOCKET, topic: subscription.topic, err },
          'delta subscriber threw'
        );
      }
    }
  }

  #onDrop(reason, event) {
    if (event?.code) this.#lastCloseCode = event.code;
    if (event?.code === CLOSE_CODE_REFUSED) {
      this.#refused = true;
      this.#reportUnavailable();
      this.#setUnavailable(true);
    }
    // The base client can report a drop twice — an `error` and then a `close`
    // for one dead socket — so report only the transition, or socket uptime
    // counts one outage as two.
    const wasLive = Boolean(this.#client);
    if (this.#socketOpened && !this.#userDisconnected && !this.#suspended) this.#droppedSinceOpen = true;
    this.#socketOpened = false;
    this.#connecting = false;
    this.#client = null;
    this.#socketSubscriptionKeys.clear();
    if (wasLive) {
      useLogger().info(
        {
          event: 'ws.close',
          socket: SOCKET,
          reason,
          code: event?.code,
          expected: Boolean(this.#userDisconnected || this.#suspended)
        },
        'delta socket closed'
      );
    }
    if (this.#userDisconnected) return;
    this.#scheduleReconnect();
  }

  #teardownSocket() {
    if (this.#client) {
      try { this.#client.close(); } catch { /* noop */ }
      this.#client = null;
    }
    this.#socketSubscriptionKeys.clear();
  }

  #scheduleReconnect() {
    if (this.#userDisconnected || this.#suspended || this.#refused || !this.#wsUrl) return;
    if (this.#reconnectTimer) return;
    const delay = this.#reconnectDelay;
    this.#reconnectAttempt += 1;
    if (this.#reconnectAttempt === UNAVAILABLE_AFTER_ATTEMPTS) {
      this.#reportUnavailable();
      this.#setUnavailable(true);
    }
    useLogger().info(
      { event: 'ws.reconnect', socket: SOCKET, attempt: this.#reconnectAttempt, delayMs: delay },
      'delta reconnect scheduled'
    );
    this.#reconnectTimer = setTimeout(() => {
      this.#reconnectTimer = null;
      this.#open();
    }, delay);
    this.#reconnectDelay = Math.min(
      Math.round(delay * RECONNECT_BACKOFF_FACTOR),
      RECONNECT_MAX_DELAY_MS
    );
  }

  #clearReconnect() {
    if (this.#reconnectTimer) {
      clearTimeout(this.#reconnectTimer);
      this.#reconnectTimer = null;
    }
    this.#reconnectDelay = RECONNECT_INITIAL_DELAY_MS;
    this.#reconnectAttempt = 0;
  }
}

const deltaRealtime = new DeltaRealtimeClient();

export { deltaRealtime, DeltaRealtimeClient };
export default deltaRealtime;
